import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    Box,
    Button,
    Grid,
    List,
    ListItem,
    ListItemText,
    makeStyles,
    Paper,
    Snackbar,
    Typography,
    useMediaQuery,
    useTheme
} from "@material-ui/core";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@material-ui/icons";
import InventorySkeleton from "../../components/skeleton/estimate/InventorySkeleton";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "@material-ui/lab";
import { getInventoryData, updateOrderData } from "../../adapters/estimate/estimateAdapters";
import { loadFromSession, loadInventory, resetInventory } from "../../redux/reducers/inventory/inventoryReducer";
import Category from "../../components/inventory/Category";
import CategoryItems from "../../components/inventory/CategoryItems";
import InventoryList from "../../components/inventory/InventoryList";
import CustomItems from "../../components/inventory/CustomItems";
import SearchInventory from "../../components/inventory/SearchInventory";
import { getAreaCount, getTotalItemsCount, getVolumeCount } from "../../components/inventory/InventoryHelper";
import WarningModal from "../../components/common/WarningModal";
import { useHistory } from "react-router-dom";
import { Map } from "immutable";
import Header from "../../components/template/estimate/Header";
import ProgressBar from "../../components/template/ProgressBar";
import { thunkLoadMoveInfoFromSession } from "../../redux/reducers/move_info/moveInfoReducer";
import { thunkLoadPropertyInfoFromSession } from "../../redux/reducers/property_info/propertyInfoReducer";
import { thunkClearEstimateData, updateStepToActive } from "../../redux/reducers/progress/progressReducer";
import navigationsLinks from "../../constants/navigation_links";
import { updateProgressSession } from "../../redux/reducers/progress/progressFn";
import { verifyOrderInDB } from "../../functions/helper";
import isEmpty from "lodash.isempty";

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(1),
    },
    scrollableBox: {
        [theme.breakpoints.up("sm")]: {
            overflowY: "auto",
            maxHeight: 360,
            scrollbarWidth: "thin",
            paddingRight: 4,
        },
    },
    formActionPaper: {
        padding: theme.spacing(1),
        display: "flex",
        justifyContent: "right",
        alignItems: "center"
    },
    addedItemsContain: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: "10px",
        cursor: "pointer",
        backgroundColor: "#FFCACA",
    },
    totalItemsCount: {
        width: 35,
        height: 35,
        borderRadius: "50%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "16px",
        fontWeight: "bold",
        color: "#EE553B",
        backgroundColor: "#FFFFFF",
        border: "1px solid #EE553B",
    },
    addedItemsMobileContain: {
        display: "flex",
        alignItems: "center",
        gap: 16,
        cursor: "pointer",
    },
}));

const AddInventory = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down("sm"));

    const [inventoryMode, setInventoryMode] = useState("table");
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [originalJSON, setOriginalJSON] = useState(null);
    const [openResetModal, setOpenResetModal] = useState(false);
    const [openNotify, setOpenNotify] = useState({ open: false, type: "success", message: "" });

    const inventoryData = useSelector((state) => state.inventoryInfo);
    const moveInfo = useSelector((state) => state.moveInfo);
    const propertyInfo = useSelector((state) => state.propertyInfo);

    const handleClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpenNotify({ ...openNotify, open: false });
    };

    const fetchInventoryData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getInventoryData();
            if (response.data.items) {
                setOriginalJSON(response.data.items);
                if (sessionStorage.getItem("inventory_info") !== null) {
                    const inventoryInfo = JSON.parse(sessionStorage.getItem("inventory_info"));
                    dispatch(loadFromSession(inventoryInfo));
                    setLoading(false);
                } else {
                    dispatch(loadInventory(response.data.items));
                    setLoading(false);
                }
            }
        } catch (error) {
            console.error("Error fetching inventory:", error);
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchInventoryData();
    }, [fetchInventoryData]);

    useEffect(() => {
        if (
            moveInfo.hasLoadedFromSession === false &&
            propertyInfo.hasLoadedFromSession === false
        ) {
            if (
                sessionStorage.getItem("move_info") !== null &&
                sessionStorage.getItem("property_info") !== null
            ) {
                const moveInfo = sessionStorage.getItem("move_info");
                const propertyInfo = sessionStorage.getItem("property_info");
                dispatch(thunkLoadMoveInfoFromSession({ moveInfo: moveInfo }));
                dispatch(thunkLoadPropertyInfoFromSession({ propertyInfo: propertyInfo }));
            }
        }
    }, [dispatch, moveInfo, propertyInfo]);

    useEffect(() => {
        if (!selectedCategory && inventoryData.getIn(["inventory", 0])) {
            setSelectedCategory(inventoryData.getIn(["inventory", 0, "id"]));
        }
    }, [selectedCategory, inventoryData]);

    const AddInventoryForm = useFormik({
        initialValues: {
            search_inventory: "",
            gotoPage: "",
        },
        validationSchema: Yup.object().shape({
            search_inventory: Yup.string(),
            gotoPage: Yup.string(),
        }),
        onSubmit: async (values) => {
            try {
                setLoading(true);
                if (Map.isMap(inventoryData)) {
                    const count = getTotalItemsCount(inventoryData);
                    if (count === 0) {
                        setOpenNotify({
                            open: true,
                            type: "warning",
                            message: "Inventory Warning! Your Inventory cannot be Zero, Please select atleast one item."
                        });
                        setLoading(false);
                        window.scrollTo(0, 0);
                        return;
                    }
                    const inventoryInfo = inventoryData.toJS();
                    const items = JSON.stringify(inventoryInfo);
                    sessionStorage.setItem("inventory_info", items);
                    updateProgressSession("step3");
                    dispatch(updateStepToActive({ stepToUpdate: "step3" }));
                    const isOrderOutdated = await verifyOrderInDB("relocation");
                    if (isOrderOutdated === true) {
                        dispatch(thunkClearEstimateData());
                        history.push("/");
                        return;
                    }
                    const totalVolume = getVolumeCount(inventoryData);
                    const totalArea = getAreaCount(inventoryData);
                    const userSessionData = JSON.parse(sessionStorage.getItem("userSessionData"));
                    const sessionOrderDetails = JSON.parse(sessionStorage.getItem("orderDetails"));
                    const estimateId = sessionOrderDetails.orderId;
                    const userId = userSessionData.userId;

                    const body = {
                        items: items,
                        total_items: count,
                        volume_cuft: totalVolume,
                        area_sqft: totalArea,
                        lead_stage: "inventory_selected",
                        version: "v2",
                        estimate_id: estimateId,
                        user_id: userId,
                        update_type: "inventory_info",
                    };
                    const params = body;
                    await updateOrderData(params);
                    if (!isEmpty(values.gotoPage)) {
                        history.push(values.gotoPage);
                    } else {
                        history.push(navigationsLinks.summary_page);
                    }
                }
                setLoading(false);
            } catch (e) {
                console.log("ERROR", e);
                setOpenNotify({ open: true, type: "error", message: "Failed! Try again later." });
                setLoading(false);
            }
        },
    });

    const onSubmitReset = () => {
        setLoading(true);
        const params = originalJSON;
        dispatch(resetInventory(params));
        setOpenResetModal(false);
        setLoading(false);
    };

    const handleSubmitWithGotoPage = (params) => {
        const { gotoPage } = params;
        AddInventoryForm.setFieldValue("gotoPage", gotoPage);
        AddInventoryForm.handleSubmit();
    };

    return (
        <React.Fragment>
            <Header currentPage="inventory" referenceToSubmit={handleSubmitWithGotoPage} />
            <main className="estimate-inventory pb-0">
                <div className="container">
                    <section className="progress-bar-section">
                        <div className="row align-items-center justify-content-center">
                            <div className="col-sm-12 col-md-12 col-lg-9">
                                <ProgressBar
                                    currentPage="inventory"
                                    referenceToSubmit={handleSubmitWithGotoPage}
                                />
                            </div>
                        </div>
                    </section>
                </div>
                {isLoading === true && <InventorySkeleton />}
                {isLoading === false && selectedCategory && (
                    <form
                        className="inventory-container container"
                        onSubmit={AddInventoryForm.handleSubmit}
                        noValidate
                        autoComplete="off"
                    >
                        <Grid container spacing={3} alignItems="center">
                            {!isMobileView && (
                                <Grid className="pb-0" item xs={12} sm={12} md={4} lg={4}>
                                    <Paper className={classes.paper} elevation={0}>
                                        <List>
                                            <ListItem
                                                className={classes.addedItemsContain}
                                                onClick={() => setInventoryMode("search")}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography style={{ fontWeight: 600 }}>
                                                            Added Items
                                                        </Typography>
                                                    }
                                                />
                                                <Box className={classes.totalItemsCount}>
                                                    {getTotalItemsCount(inventoryData)}
                                                </Box>
                                            </ListItem>
                                        </List>
                                    </Paper>
                                </Grid>
                            )}
                            <Grid className={isMobileView ? "pt-0" : "pb-0"} item xs={12} sm={12} md={8} lg={8}>
                                <Paper className={classes.paper} elevation={0}>
                                    <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                                        <Grid item xs>
                                            <SearchInventory form={AddInventoryForm} />
                                        </Grid>
                                        <Grid item xs="auto">
                                            <Button
                                                className="mr-2"
                                                variant="contained"
                                                color="default"
                                                onClick={() => setOpenResetModal(true)}
                                            >
                                                Reset
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={12} md={4} lg={4}>
                                <Paper className={classes.paper} elevation={0}>
                                    <Grid container spacing={2} alignItems="center">
                                        {isMobileView && (
                                            <Grid item xs={12}>
                                                <Box
                                                    className={classes.addedItemsMobileContain}
                                                    onClick={() => setInventoryMode("search")}
                                                >
                                                    <Typography
                                                        style={{ fontWeight: 500 }}
                                                        variant="subtitle1"
                                                        color="textPrimary"
                                                    >
                                                        Added Items
                                                    </Typography>
                                                    <Box
                                                        className="badge badge-pill total-items-modal-count"
                                                        style={{ background: "#FFCACA" }}
                                                    >
                                                        {getTotalItemsCount(inventoryData)}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <Category
                                                setInventoryMode={(v) => setInventoryMode(v)}
                                                selectedCategory={selectedCategory}
                                                setSelectedCategory={(v) => setSelectedCategory(v)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} lg={8}>
                                <Paper className={classes.paper} elevation={0} style={{ minHeight: isMobileView ? "" : "378px" }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12}>
                                            <Box className={classes.scrollableBox}>
                                                {inventoryMode === "search" && (
                                                    <InventoryList />
                                                )}

                                                {inventoryMode === "table" && (
                                                    <>
                                                        {selectedCategory === "custom_items" ? (
                                                            <CustomItems />
                                                        ) : (
                                                            <CategoryItems selectedCategory={selectedCategory} />
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={12} lg={12}>
                                <Paper className={classes.formActionPaper} elevation={0}>
                                    <Button
                                        className="mr-2"
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => handleSubmitWithGotoPage({ gotoPage: navigationsLinks.property_info_page })}
                                    >
                                        <KeyboardArrowLeft /> Floor Info
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isLoading || AddInventoryForm.isSubmitting}
                                    >
                                        Summary <KeyboardArrowRight />
                                    </Button>
                                </Paper>
                            </Grid>
                        </Grid>
                    </form>
                )}
                <WarningModal
                    open={openResetModal}
                    onClose={() => setOpenResetModal(false)}
                    onSubmit={onSubmitReset}
                    title={"Reset Inventory"}
                    message={"Are you sure that you want to Reset a Inventory?"}
                    loading={isLoading}
                />
                <Snackbar
                    open={openNotify.open}
                    autoHideDuration={3000}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <Alert variant="filled" elevation={6} severity={openNotify.type}>
                        {openNotify.message}
                    </Alert>
                </Snackbar>
            </main>
        </React.Fragment>
    );
}

export default AddInventory;